﻿import { useState } from 'react';
import { CollapsibleSection, ToggleRow } from './shared';
import { useSettingStore } from '../../../stores/settingStore';

type SectionKey = 'inApp' | 'sms' | 'email' | 'browser';

const ALL_OPEN: Record<SectionKey, boolean> = { inApp: true, sms: true, email: true, browser: true };
const ALL_CLOSED: Record<SectionKey, boolean> = { inApp: false, sms: false, email: false, browser: false };

const DEFAULT_PREFS: Record<string, boolean> = {
  newOrder: true,
  orderStatus: true,
  systemAlert: true,
  weeklyReport: false,
  smsNewOrder: false,
  smsCritical: true,
  emailDigest: true,
  emailMarketing: false,
  browserPush: true,
  soundAlert: false,
};

export default function NotificationPanel() {
  const { settings, updateSettings } = useSettingStore();
  const prefs = { ...DEFAULT_PREFS, ...(settings.notificationPrefs ?? {}) };

  const [sections, setSections] = useState<Record<SectionKey, boolean>>({
    inApp: true,
    sms: false,
    email: false,
    browser: false,
  });

  const toggle = (key: string) =>
    void updateSettings({ notificationPrefs: { ...prefs, [key]: !prefs[key] } });

  const toggleSection = (key: SectionKey) =>
    setSections(s => ({ ...s, [key]: !s[key] }));

  const allOpen = Object.values(sections).every(Boolean);

  return (
    <div className="space-y-3 md:space-y-4">

      {/* Expand / Collapse all — mobile only */}
      <div className="flex items-center justify-between md:hidden">
        <p className="text-xs text-gray-400">共 4 个通知分组</p>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setSections(ALL_OPEN)}
            disabled={allOpen}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg transition-colors cursor-pointer hover:border-teal-300 hover:text-teal-600 disabled:opacity-40 disabled:cursor-default whitespace-nowrap"
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-expand-up-down-line text-xs" />
            </div>
            全部展开
          </button>
          <button
            type="button"
            onClick={() => setSections(ALL_CLOSED)}
            disabled={!allOpen && Object.values(sections).every(v => !v)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg transition-colors cursor-pointer hover:border-gray-300 hover:text-gray-600 disabled:opacity-40 disabled:cursor-default whitespace-nowrap"
          >
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-collapse-diagonal-line text-xs" />
            </div>
            全部收折
          </button>
        </div>
      </div>

      <CollapsibleSection title="站内通知" open={sections.inApp} onOpenChange={() => toggleSection('inApp')}>
        <div>
          <ToggleRow icon="ri-shopping-bag-3-line" title="新订单提醒" desc="有新订单创建时在站内提醒" checked={prefs.newOrder} onChange={() => toggle('newOrder')} />
          <ToggleRow icon="ri-refresh-line" title="订单状态变更" desc="订单状态更新时发送提醒" checked={prefs.orderStatus} onChange={() => toggle('orderStatus')} />
          <ToggleRow icon="ri-error-warning-line" title="系统预警" desc="系统异常或重要操作通知" checked={prefs.systemAlert} onChange={() => toggle('systemAlert')} />
          <ToggleRow icon="ri-bar-chart-2-line" title="每周报表" desc="每周一发送上周数据汇总" checked={prefs.weeklyReport} onChange={() => toggle('weeklyReport')} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="短信通知" open={sections.sms} onOpenChange={() => toggleSection('sms')}>
        <div>
          <ToggleRow icon="ri-message-2-line" title="新订单短信" desc="新订单创建时发送短信通知" checked={prefs.smsNewOrder} onChange={() => toggle('smsNewOrder')} />
          <ToggleRow icon="ri-alarm-warning-line" title="紧急事件短信" desc="系统紧急情况强制推送短信" checked={prefs.smsCritical} onChange={() => toggle('smsCritical')} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="邮件通知" open={sections.email} onOpenChange={() => toggleSection('email')}>
        <div>
          <ToggleRow icon="ri-mail-line" title="邮件日报" desc="每日汇总发送到绑定邮箱" checked={prefs.emailDigest} onChange={() => toggle('emailDigest')} />
          <ToggleRow icon="ri-mail-star-line" title="活动营销邮件" desc="促销活动与功能更新通知" checked={prefs.emailMarketing} onChange={() => toggle('emailMarketing')} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="浏览器推送" open={sections.browser} onOpenChange={() => toggleSection('browser')}>
        <div>
          <ToggleRow icon="ri-computer-line" title="桌面推送通知" desc="允许浏览器弹出通知" checked={prefs.browserPush} onChange={() => toggle('browserPush')} />
          <ToggleRow icon="ri-volume-up-line" title="声音提醒" desc="收到通知时播放提示音" checked={prefs.soundAlert} onChange={() => toggle('soundAlert')} />
        </div>
      </CollapsibleSection>
    </div>
  );
}
